import React from 'react';
import { AlertTriangle, RefreshCw, Home, Copy, Check } from 'lucide-react';

// App-wide crash safety net. Customers see a friendly recovery screen; when an
// admin is logged in (auth token present) the exact error + component stack are
// shown with a one-click "Copy error" button so it can be reported for a fix.
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: '', copied: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    this.setState({ error, componentStack: (info && info.componentStack) || '' });
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, info);
  }

  buildReport() {
    const { error, componentStack } = this.state;
    return [
      `Error: ${(error && (error.message || error.toString())) || 'Unknown error'}`,
      '',
      'Stack:',
      (error && error.stack) || '(no stack)',
      '',
      'Component stack:',
      componentStack || '(none)',
      '',
      `URL: ${typeof window !== 'undefined' ? window.location.href : ''}`,
      `Time: ${new Date().toISOString()}`,
    ].join('\n');
  }

  handleCopy = () => {
    const text = this.buildReport();
    const done = () => { this.setState({ copied: true }); setTimeout(() => this.setState({ copied: false }), 2500); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(done);
    } else {
      done();
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isAdmin = typeof window !== 'undefined' && !!localStorage.getItem('token');
    const { error, componentStack, copied } = this.state;

    return (
      <div className="min-h-screen flex items-center justify-center bg-shrimp-bg px-4 py-12" data-testid="error-boundary">
        <div className="w-full max-w-lg bg-surface border border-shrimp-border/50 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] p-8 text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertTriangle className="w-7 h-7 text-amber-600" />
          </div>
          <h1 className="font-display text-2xl font-bold text-shrimp-dark mb-2">Something went wrong</h1>
          <p className="text-shrimp-text mb-6">
            Sorry about that — a small hiccup stopped this page from loading. Please refresh, and if it keeps happening try again shortly. Your basket is safe.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center justify-center gap-2 bg-shrimp-green hover:bg-shrimp-text text-white font-medium px-5 py-2.5 rounded-full transition-colors active:scale-95"
              data-testid="error-reload-button"
            >
              <RefreshCw className="w-4 h-4" /> Refresh page
            </button>
            <button
              onClick={() => window.location.assign('/')}
              className="inline-flex items-center justify-center gap-2 bg-shrimp-beige hover:bg-shrimp-border/40 text-shrimp-dark font-medium px-5 py-2.5 rounded-full transition-colors active:scale-95"
              data-testid="error-home-button"
            >
              <Home className="w-4 h-4" /> Go to homepage
            </button>
          </div>

          {isAdmin && (
            <div className="mt-8 text-left" data-testid="error-admin-details">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-shrimp-text/70">Admin diagnostics</p>
                <button
                  onClick={this.handleCopy}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-shrimp-green hover:text-shrimp-text transition-colors"
                  data-testid="error-copy-button"
                >
                  {copied ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy error</>}
                </button>
              </div>
              <div className="bg-shrimp-bg border border-shrimp-border/60 rounded-lg p-3 max-h-64 overflow-auto">
                <p className="text-sm font-semibold text-red-600 break-words mb-2" data-testid="error-message">
                  {(error && (error.message || error.toString())) || 'Unknown error'}
                </p>
                {error && error.stack && (
                  <pre className="text-[11px] leading-relaxed text-shrimp-text whitespace-pre-wrap break-words mb-2">{error.stack}</pre>
                )}
                {componentStack && (
                  <pre className="text-[11px] leading-relaxed text-shrimp-text/80 whitespace-pre-wrap break-words border-t border-shrimp-border/40 pt-2">{componentStack}</pre>
                )}
              </div>
              <p className="text-[11px] text-shrimp-text/70 mt-2">
                Copy this and paste it in the chat so it can be fixed quickly.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
